import { Injectable } from '@angular/core';
import { throwError, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Apollo, gql } from 'apollo-angular';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { Mutation, Provider, Query } from '../../generated/graphql';
import { UpdateSupportMetadata } from '../models/provider';
import { URLS } from '../constant/constant';

@Injectable({
  providedIn: 'root'
})
export class GraphqlService {
  constructor(private readonly apollo: Apollo) { }

  private handleError(error: HttpErrorResponse) {
    return throwError(error);
  }

  getMarketingCounts() {
    const getMarketingCounts = gql`
      {
        aggregateUserPackages(
          groupBy: {
            query: { package_id: { function: COUNT, includeField: true } }
            group: ["package_id"]
            skip: 0
            take: 10
          }
          order: { COUNT: DESC }
        ) {
          COUNT
          package_id
          package_type {
            package_name
            country {
              country_name
            }
          }
        }

        aggregateUsers(
          groupBy: {
            query: { user_source_id: { function: COUNT, includeField: true } }
            group: ["user_source_id"]
            skip: 0
            take: 10
          }
          order: { COUNT: DESC }
        ) {
          COUNT
          user_source_id
          user_source {
            source
          }
        }

        aggregateConsultation(
          groupBy: {
            query: {
              consultation_status: { function: COUNT, includeField: true }
            }
            group: ["consultation_status"]
          }
          order: { COUNT: DESC }
        ) {
          COUNT
          consultation_status
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({ query: getMarketingCounts })
      .valueChanges.pipe(catchError(this.handleError));
  }


  getAllCounts() {
    const getCounts = gql`
      {
        aggregatePatients(
    groupBy: {
    	query: {
        country_id: {
          function: COUNT
          includeField: true
        }
      },
      group: ["country_id"]
      }) {
      COUNT
      country {
        country_name
      }
    }
        countDoctors
        countVendors
        countSpeciality
        aggregateConsultation(groupBy: { query: { id: { function: COUNT } } }) {
          totalCountOfConsultations: COUNT
        }

        findCountries {
          created_at
          updated_at
          id
          country_name
        }

        aggregateUsers(groupBy: { query: { user_guid: { function: COUNT } } }) {
          totalCountOfUsers: COUNT
        }

        aggregateConsultationAppointment(
          groupBy: {
            query: { specialty_id: { function: COUNT, includeField: true } }
            group: ["specialty_id"]
            skip: 0
            take: 11
          }
          order: { COUNT: DESC }
        ) {
          consultationCount: COUNT
          specialty_id
          d_specialty_type {
            specialty_name
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({ query: getCounts })
      .valueChanges.pipe(catchError(this.handleError));
  }

  getDietData() {
    const SERVER_COUNTRY = URLS.IS_UK ? 232 : 101
    const DIET_DATA_QUERY = gql`
    query GetPriorityTypes($countryId: Int!) {
      findPriorityTypes(
        filter: {
          consultation_type_id: { _eq: 5 }
          is_enabled: { _eq: true }
          country_id: { _eq: $countryId }
          amount: { _gt: 0 }
        }
        order: { id: ASC }
      ) {
        id
        transaction_type
        consultation_name
        is_enabled
        amount
        country_id
        consultation_type_id
        description
        number_of_consultations
        renderedAmount
      }
    }
  `;

    return this.apollo
      .watchQuery<Query>({ query: DIET_DATA_QUERY, variables: { countryId: SERVER_COUNTRY } })
      .valueChanges.pipe(catchError(this.handleError));
  }


  addProvider(input: {
    contact_name: string;
    name: string;
    country_id: number;
    email: string;
    phone_number: string;
    recaptcha: string;
    password: string;
    identification_number: string;
  }) {
    const addProviderMutation = gql`
      mutation AddProvider($addProviderInput: AddProviderInput!) {
        addProvider(data: $addProviderInput) {
          user_guid
          retry_after
          service_id
          retry_after_in_seconds
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: addProviderMutation,
      variables: {
        addProviderInput: input
      }
    });
  }

  updateProvider(input: { is_verified: boolean; provider_id: string }) {
    const updateProviderMutation = gql`
      mutation updateProvider($updateProviderInput: UpdateProviderInput!) {
        updateProvider(data: $updateProviderInput) {
          is_verified
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: updateProviderMutation,
      variables: {
        updateProviderInput: input
      },
      update: (cache) => cache.reset()
    });
  }

  updateProviderMetadata(metadata: UpdateSupportMetadata) {
    const updateProviderMetadataMutation = gql`
      mutation updateProviderMetadata($metadata: JSON!) {
        updateProviderMetadata(metadata: $metadata) {
          metadata
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: updateProviderMetadataMutation,
      variables: {
        metadata: metadata
      }
    });
  }


  fetchProviderMetadata(providerId: string) {
    const fetchProviderMetadata = gql`
      query fetchProviderMetadata($providerId: ID!) {
        fetchProviderMetadata(provider_id: $providerId) {
          id
        metadata
        provider {
          name
          is_primary_provider
          slug
          provider_subscription {
            status
            is_trial
          }
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchProviderMetadata,
        variables: { providerId }
      })
      .valueChanges.pipe(
        map((result) => result.data.fetchProviderMetadata),
        catchError(this.handleError)
      );
  }

  findProviderServiceTokenType(providerService: string) {
    const findServiceTokens = gql`
      query findProviderServiceTokenType($providerService: ProviderService!) {
        findProviderServiceTokenType(providerService: $providerService) {
          id
          service
          token_type
        }
      }
    `;

    return this.apollo
      .watchQuery({
        query: findServiceTokens,
        variables: {
          providerService
        }
      })
      .valueChanges.pipe(catchError(this.handleError));
  }

  fetchLicenses() {
    const fetchLicenses = gql`
      {
        fetchLicenses {
          id
          name
          expires_after
          expiry_type
          price
          is_active
          number_of_users
          number_of_patients
          renderedPrice
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({ query: fetchLicenses })
      .valueChanges.pipe(catchError(this.handleError));
  }

  fetchProviderLicenses(isActive: Boolean) {
    const fetchProviderLicenses = gql`
      query fetchProviderLicenses($isActive: Boolean!) {
        fetchProviderLicenses(isActive: $isActive) {
        providerLicense
        {
        id
          expiry_date
          is_active
          price_paid
          number_of_users
          number_of_patients
          created_at
          updated_at
          provider {
            id
            name
            provider_subscription{
            status
            }
          }
          license {
            name
          }
        }
          has_trial_completed
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchProviderLicenses,
        variables: { isActive }
      })
      .valueChanges.pipe(catchError(this.handleError));
  }


  getSubscriptionStatus(isActive: Boolean) {
    const fetchProviderLicenses = gql`
      query fetchProviderLicenses($isActive: Boolean!) {
        fetchProviderLicenses(isActive: $isActive) {
        providerLicense
        {
        id
          expiry_date
          is_active
          price_paid
          number_of_users
          number_of_patients
          created_at
          updated_at
          provider {
            id
            name
            provider_subscription{
            status
            }
          }
          license {
            name
          }
        }
          has_trial_completed
        }
      }
    `;

    return this.apollo
      .query<Query>({
        query: fetchProviderLicenses,
        variables: { isActive }
      });
  }

  fetchAllowedCountries(provider_id: string) {
    const fetchAllowedCountries = gql`
      query fetchAllowedCountries($provider_id: ID!) {
        fetchAllowedCountries(provider_id: $provider_id) {
          country {
            id
            country_name
            currency_type
            acronym
            phone_code
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchAllowedCountries,
        variables: { provider_id }
      })
      .valueChanges.pipe(catchError(this.handleError));
  }

  fetchProviderLicenseTransactions() {
    const fetchProviderLicenseTransactions = gql`
      query fetchProviderLicenseTransactions {
        fetchProviderLicenseTransactions {
          id
          price_paid
          status
          created_at
          provider_license {
            id
            expiry_date
            license{
              name
            }
          }
          subscription_invoice {
            id
            created_at
            stripe_payment_status
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchProviderLicenseTransactions,
      })
      .valueChanges.pipe(catchError(this.handleError));
  }


  fetchInvoicePDF(invoiceId: string) {
    const fetchInvoicePDF = gql`
      query fetchInvoicePDF($invoiceId: String!) {
        fetchInvoicePDF(invoiceId: $invoiceId)
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchInvoicePDF,
        variables: { invoiceId }
      })
      .valueChanges.pipe(
        catchError(this.handleError)
      );
  }


  registerLicense(licenseId: string) {
    const registerLicense = gql`
      mutation RegisterLicense($licenseId: ID!) {
        registerLicense(license_id: $licenseId) {
          payment_method
          payment_id
          provider_license_transaction_id
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: registerLicense,
      variables: {
        licenseId: licenseId
      }
    });
  }

  addAllowedCountryForProvider(countryId: number[]) {
    const addAllowedCountryForProvider = gql`
      mutation addAllowedCountryForProvider($country_id: [Int!]!) {
        addAllowedCountryForProvider(country_id: $country_id) {
          id
          provider {
            id
            name
          }
          country {
            id
          }
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: addAllowedCountryForProvider,
      variables: {
        country_id: countryId
      },
      update: (cache) => cache.reset()
    });
  }

  removeAllowedCountry(countryId: number) {
    const removeAllowedCountry = gql`
      mutation removeAllowedCountry($country_id: [Int!]!) {
        removeAllowedCountry(country_id: $country_id)
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: removeAllowedCountry,
      variables: {
        country_id: [countryId]
      },
      update: (cache) => cache.reset()
    });
  }

  fetchSecretNames() {
    const fetchSecretKeys = gql`
      {
        fetchSecretNames {
          secretName
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({ query: fetchSecretKeys })
      .valueChanges.pipe(catchError(this.handleError));
  }

  findProvider(slug: string) {
    const findProvider = gql`
      query findProvider($slug: String!) {
        findProvider(slug: $slug) {
          id
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: findProvider,
        variables: { slug }
      })
      .valueChanges.pipe(
        map((result) => result.data.findProvider),
        catchError(this.handleError)
      );
  }

  findProviders() {
    const findProvidersQuery = gql`
      query findProviders {
        findProviders {
          id
          name
          email
          is_verified
          contact_name
          created_at
          phone_number
        }
      }
    `;
    return this.apollo.query<Query>({
      query: findProvidersQuery
    });
  }

  addProviderSecret(data: {
    provider_id: string;
    provider_service_token_type_id: string;
    secret: string;
  }) {
    const addProviderSecret = gql`
      mutation addProviderSecret($data: AddProviderTokenInput!) {
        addProviderSecret(data: $data)
      }
    `;

    return this.apollo.mutate({
      mutation: addProviderSecret,
      variables: {
        data: data
      }
    });
  }

  fetchProviderLicenseTransaction(transactionId: string) {
    const fetchProviderLicenseTransaction = gql`
      query fetchProviderLicenseTransaction($transactionId: String!) {
        fetchProviderLicenseTransaction(transactionId: $transactionId) {
          id
          price_paid
          status
          created_at
          updated_at
        }
      }
    `;

    return this.apollo
      .watchQuery<Query>({
        query: fetchProviderLicenseTransaction,
        variables: { transactionId }
      })
      .valueChanges.pipe(
        map((result) => result.data.fetchProviderLicenseTransaction),
        catchError(this.handleError)
      );
  }

  findPriorityTypes() {
    const findPriorityTypes = gql`
      query FindPriorityTypes {
            
        findPriorityTypes(filter: {consultation_type_id: { _in: [1,2,3,4] }}
          order: { id: ASC }) {
          id
          transaction_type
          consultation_name
          priority_type
          specialty_id
          is_enabled
          amount
          renderedAmount
          country_id
          consultation_type_id
          response_min
          response_max
          description
          is_onetime
          number_of_consultations
          image_url
          storage_space
          created_at
          updated_at
        }
      }
    `;

    return this.apollo.query({
      query: findPriorityTypes,
    }).pipe(
      map((result: any) => result.data.findPriorityTypes)
    );
  }

  createPriorityType(input: {
    specialty_id: number;
    is_enabled: boolean;
    amount: number;
    country_id: number;
    consultation_type_id: number;
    doctor_origin_country_ids: number[];
  }) {
    const createPriorityMutation = gql`
      mutation createPriorityType($createPriorityTypeInput: CreatePriorityTypeInput!) {
        createPriorityType(data: $createPriorityTypeInput) {
          transaction_type
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: createPriorityMutation,
      variables: {
        createPriorityTypeInput: input
      }
    });
  }


  updatePriorityType(input: { id: number; is_enabled: boolean }) {
    const updatePriorityTypeMutation = gql`
      mutation updatePriorityType($updatePriorityTypeInput: UpdatePriorityTypeInput!) {
        updatePriorityType(data: $updatePriorityTypeInput) {
          is_enabled
        }
      }
    `;

    return this.apollo.mutate<Mutation>({
      mutation: updatePriorityTypeMutation,
      variables: {
        updatePriorityTypeInput: input
      },
    });
  }
}
